import React, { useState, useEffect, useRef } from "react";
import { Card, Button, List, Modal, Form, Input, Space, message, Popconfirm, Divider, Tag, Typography } from "antd";
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    MinusCircleOutlined,
    BookOutlined,
    LinkOutlined,
    FilePdfOutlined,
    VideoCameraOutlined,
    CopyOutlined,
} from "@ant-design/icons";
import axios from "axios";
import Cookies from "universal-cookie";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const { Text } = Typography;

const LessonManager = ({ courseId }) => {
    const [lessons, setLessons] = useState([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [editingLesson, setEditingLesson] = useState(null);
    const [loading, setLoading] = useState(false);
    const [content, setContent] = useState("");
    const [form] = Form.useForm();
    const quillRef = useRef(null);

    // picker state
    const [pickerOpen, setPickerOpen] = useState(false);
    const [allTeacherLessons, setAllTeacherLessons] = useState([]);
    const [pickerLoading, setPickerLoading] = useState(false);
    const [copyingId, setCopyingId] = useState(null);

    const cookies = new Cookies();
    const auth = cookies.get("authToken");
    const authConfig = { headers: { "auth-token": auth } };
    const multipartConfig = { headers: { "auth-token": auth, "Content-Type": "multipart/form-data" } };

    const quillModules = {
        // toolbar: [
        //     [{ header: [1, 2, 3, false] }],
        //     ["bold", "italic", "underline"],
        //     [{ list: "ordered" }, { list: "bullet" }],
        //     ["link", "image"],
        // ],
        toolbar: {
            container: [
                [{ header: [1, 2, 3, 4, 5, 6, false] }],
                ["bold", "italic", "underline", "strike", "blockquote"],
                [{ list: "ordered" }, { list: "bullet" }, { indent: "-1" }, { indent: "+1" }],
                ["link", "image", "video"],
                // ["clean"],
            ],
        },
    };

    useEffect(() => {
        if (courseId) fetchLessons();
    }, [courseId]);

    const fetchLessons = async () => {
        try {
            const res = await axios.get(`/api/courses/${courseId}/lessons`, authConfig);
            setLessons(res.data);
        } catch (err) {
            message.error("ยังไม่มีบทเรียน");
        }
    };

    const openAdd = () => {
        setEditingLesson(null);
        setContent("");
        form.resetFields();
        setModalOpen(true);
    };

    const openEdit = (lesson) => {
        setEditingLesson(lesson);
        setContent(lesson.content || "");
        form.setFieldsValue({
            title: lesson.title,
            description: lesson.description,
            video_url: lesson.video_url,
            links: lesson.links || [],
        });
        setModalOpen(true);
    };

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            const formData = new FormData();
            formData.append("title", values.title);
            formData.append("description", values.description || "");
            formData.append("content", content || "");
            formData.append("video_url", values.video_url || "");
            if (values.links && values.links.length > 0) {
                formData.append("links", JSON.stringify(values.links));
            }
            if (values.video?.[0]?.originFileObj) {
                formData.append("video", values.video[0].originFileObj);
            }

            if (editingLesson) {
                await axios.put(`/api/courses/lessons/${editingLesson.id}`, formData, multipartConfig);
                message.success("แก้ไขบทเรียนสำเร็จ");
            } else {
                await axios.post(`/api/courses/${courseId}/lessons`, formData, multipartConfig);
                message.success("เพิ่มบทเรียนสำเร็จ");
            }

            setModalOpen(false);
            fetchLessons();
        } catch (err) {
            if (err?.response?.data?.message) {
                message.error(err.response.data.message);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (lessonId) => {
        try {
            await axios.delete(`/api/courses/lessons/${lessonId}`, authConfig);
            message.success("ลบบทเรียนสำเร็จ");
            fetchLessons();
        } catch (err) {
            message.error("ลบบทเรียนไม่สำเร็จ");
        }
    };

    const openPicker = async () => {
        setPickerOpen(true);
        setPickerLoading(true);
        try {
            const res = await axios.get(`/api/courses/teacher/all-lessons?excludeCourseId=${courseId}`, authConfig);
            setAllTeacherLessons(res.data);
        } catch (err) {
            message.error("โหลดบทเรียนไม่สำเร็จ");
        } finally {
            setPickerLoading(false);
        }
    };

    const handleCopyLesson = async (lessonId) => {
        setCopyingId(lessonId);
        try {
            await axios.post(`/api/courses/${courseId}/lessons/copy/${lessonId}`, {}, authConfig);
            message.success("คัดลอกบทเรียนสำเร็จ");
            fetchLessons();
        } catch (err) {
            message.error("คัดลอกไม่สำเร็จ");
        } finally {
            setCopyingId(null);
        }
    };

    // จัดกลุ่ม lessons ตาม course
    const groupedLessons = allTeacherLessons.reduce((acc, lesson) => {
        const key = lesson.course_title;
        if (!acc[key]) acc[key] = [];
        acc[key].push(lesson);
        return acc;
    }, {});

    return (
        <Card
            title={
                <Space>
                    <BookOutlined />
                    <span>บทเรียน ({lessons.length} บท)</span>
                </Space>
            }
            extra={
                <Space>
                    <Button icon={<CopyOutlined />} onClick={openPicker}>
                        เลือกจากบทเรียนที่มีอยู่
                    </Button>
                    <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>
                        เพิ่มบทเรียน
                    </Button>
                </Space>
            }
            style={{ marginTop: 24 }}
        >
            {lessons.length === 0 ? (
                <div style={{ textAlign: "center", padding: "24px", color: "#999" }}>
                    ยังไม่มีบทเรียน กดเพิ่มบทเรียนเพื่อเริ่มต้น
                </div>
            ) : (
                <List
                    dataSource={lessons}
                    renderItem={(lesson, index) => (
                        <List.Item
                            key={lesson.id}
                            actions={[
                                <Button
                                    key="edit"
                                    icon={<EditOutlined />}
                                    size="small"
                                    onClick={() => openEdit(lesson)}
                                >
                                    แก้ไข
                                </Button>,
                                <Popconfirm
                                    key="delete"
                                    title="ยืนยันลบบทเรียนนี้?"
                                    onConfirm={() => handleDelete(lesson.id)}
                                    okText="ลบ"
                                    cancelText="ยกเลิก"
                                >
                                    <Button icon={<DeleteOutlined />} size="small" danger>
                                        ลบ
                                    </Button>
                                </Popconfirm>,
                            ]}
                        >
                            <List.Item.Meta
                                avatar={
                                    <div
                                        style={{
                                            width: 32,
                                            height: 32,
                                            borderRadius: "50%",
                                            background: "#1677ff",
                                            color: "#fff",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            fontWeight: "bold",
                                        }}
                                    >
                                        {index + 1}
                                    </div>
                                }
                                title={lesson.title}
                                description={
                                    <Space size={4} wrap>
                                        {lesson.video_path && (
                                            <Tag icon={<VideoCameraOutlined />} color="blue">
                                                วิดีโอ
                                            </Tag>
                                        )}
                                        {lesson.video_url && (
                                            <Tag icon={<VideoCameraOutlined />} color="cyan">
                                                URL วิดีโอ
                                            </Tag>
                                        )}
                                        {lesson.documents?.length > 0 && (
                                            <Tag icon={<FilePdfOutlined />} color="orange">
                                                เอกสาร {lesson.documents.length} ไฟล์
                                            </Tag>
                                        )}
                                        {lesson.links?.length > 0 && (
                                            <Tag icon={<LinkOutlined />} color="green">
                                                ลิงค์ {lesson.links.length} รายการ
                                            </Tag>
                                        )}
                                        {lesson.description && (
                                            <Text type="secondary" style={{ fontSize: 12 }}>
                                                {lesson.description}
                                            </Text>
                                        )}
                                    </Space>
                                }
                            />
                        </List.Item>
                    )}
                />
            )}

            {/* Modal สร้าง/แก้ไข lesson */}
            <Modal
                title={editingLesson ? "แก้ไขบทเรียน" : "เพิ่มบทเรียน"}
                open={modalOpen}
                onOk={handleSave}
                onCancel={() => setModalOpen(false)}
                okText={editingLesson ? "บันทึก" : "เพิ่ม"}
                cancelText="ยกเลิก"
                confirmLoading={loading}
                width={800}
                destroyOnClose
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        name="title"
                        label="ชื่อบทเรียน"
                        rules={[{ required: true, message: "กรุณากรอกชื่อบทเรียน" }]}
                    >
                        <Input placeholder="เช่น บทที่ 1: บทนำ" />
                    </Form.Item>

                    <Form.Item name="description" label="คำอธิบายสั้น">
                        <Input placeholder="คำอธิบายสั้นๆ ของบทเรียน" />
                    </Form.Item>

                    <Form.Item label="เนื้อหาบทเรียน">
                        <ReactQuill
                            ref={quillRef}
                            theme="snow"
                            value={content}
                            onChange={setContent}
                            modules={quillModules}
                            style={{ minHeight: 150, marginBottom: 8 }}
                        />
                    </Form.Item>

                    <Divider orientation="left">วิดีโอ</Divider>

                    <Form.Item name="video_url" label="URL วิดีโอ (YouTube, Vimeo หรืออื่นๆ)">
                        <Input placeholder="https://www.youtube.com/watch?v=..." />
                    </Form.Item>

                    <Divider orientation="left">ลิงค์ภายนอก</Divider>

                    <Form.List name="links">
                        {(fields, { add, remove }) => (
                            <>
                                {fields.map((field) => (
                                    <Space
                                        key={field.key}
                                        style={{ display: "flex", marginBottom: 8 }}
                                        align="baseline"
                                    >
                                        <Form.Item
                                            {...field}
                                            name={[field.name, "title"]}
                                            rules={[{ required: true, message: "กรอกชื่อลิงค์" }]}
                                            style={{ marginBottom: 0 }}
                                        >
                                            <Input placeholder="ชื่อลิงค์" style={{ width: 180 }} />
                                        </Form.Item>
                                        <Form.Item
                                            {...field}
                                            name={[field.name, "url"]}
                                            rules={[{ required: true, message: "กรอก URL" }]}
                                            style={{ marginBottom: 0 }}
                                        >
                                            <Input placeholder="https://..." style={{ width: 320 }} />
                                        </Form.Item>
                                        <MinusCircleOutlined onClick={() => remove(field.name)} />
                                    </Space>
                                ))}
                                <Button
                                    type="dashed"
                                    onClick={() => add()}
                                    icon={<PlusOutlined />}
                                    style={{ marginTop: 4 }}
                                >
                                    เพิ่มลิงค์
                                </Button>
                            </>
                        )}
                    </Form.List>
                </Form>
            </Modal>
            {/* Picker Modal - เลือกบทเรียนที่มีอยู่ */}
            <Modal
                title="เลือกบทเรียนที่มีอยู่"
                open={pickerOpen}
                onCancel={() => setPickerOpen(false)}
                footer={null}
                width={640}
            >
                {pickerLoading ? (
                    <div style={{ textAlign: "center", padding: 32 }}>กำลังโหลด...</div>
                ) : Object.keys(groupedLessons).length === 0 ? (
                    <div style={{ textAlign: "center", padding: 32, color: "#999" }}>ไม่มีบทเรียนจากคอร์สอื่น</div>
                ) : (
                    Object.entries(groupedLessons).map(([courseTitle, lessonList]) => (
                        <div key={courseTitle} style={{ marginBottom: 16 }}>
                            <Divider orientation="left">
                                <Text strong>{courseTitle}</Text>
                            </Divider>
                            <List
                                size="small"
                                dataSource={lessonList}
                                renderItem={(lesson) => (
                                    <List.Item
                                        key={lesson.id}
                                        actions={[
                                            <Button
                                                key="copy"
                                                type="primary"
                                                size="small"
                                                icon={<CopyOutlined />}
                                                loading={copyingId === lesson.id}
                                                onClick={() => handleCopyLesson(lesson.id)}
                                            >
                                                คัดลอก
                                            </Button>,
                                        ]}
                                    >
                                        <List.Item.Meta
                                            title={lesson.title}
                                            description={
                                                <Space size={4}>
                                                    {lesson.video_url && (
                                                        <Tag icon={<VideoCameraOutlined />} color="cyan">
                                                            URL วิดีโอ
                                                        </Tag>
                                                    )}
                                                    {lesson.links?.length > 0 && (
                                                        <Tag icon={<LinkOutlined />} color="green">
                                                            ลิงค์ {lesson.links.length}
                                                        </Tag>
                                                    )}
                                                    {lesson.description && (
                                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                                            {lesson.description}
                                                        </Text>
                                                    )}
                                                </Space>
                                            }
                                        />
                                    </List.Item>
                                )}
                            />
                        </div>
                    ))
                )}
            </Modal>
        </Card>
    );
};

export default LessonManager;
